// Adaptador HTTP del server de replay VAR (implementa ReplayClipPort de @scout/core).
//
// El server de replay es un binario C++ en la LAN de la cancha que expone una
// API HTTP simple con token Bearer. Como el server puede variar entre canchas
// (versiones distintas, rutas custom), las rutas son overrideables vía
// `opts.paths`. Todo lo demás (headers, timeout, mapeo de respuesta) es fijo.
//
// ⚠️ QUÉ ESTÁ CONFIRMADO Y QUÉ NO (leer antes de prometer nada en una demo).
//
// Ninguna de estas rutas está verificada contra el binario en este repo: son
// el contrato DECLARADO por nosotros. Las cuatro originales (`/api/status`,
// `/api/mark`, `/api/seek`, `/api/export/clip`) vienen de la primera
// integración; las dos nuevas (`/api/cameras`, `/api/export/clip/{jobId}`) las
// agrega esta ola siguiendo el mismo estilo. Y `ARCHITECTURE.md` afirma además
// que el server expone `/api/markers`, que NO existe en ninguna línea de este
// repo. Antes de comprometer el challenge hay que confirmar con quien mantiene
// el binario C++ qué expone realmente: de eso depende cuánto de esto es
// adaptador y cuánto es desarrollo en C++ (riesgo #1 de
// `docs/acta-electronica.md` §10).
//
// Lo que SÍ está verificado es que este cliente cumple el contrato tal como
// está declarado: `test/var-server.test.ts` levanta un server HTTP de verdad
// que lo implementa y corre el adaptador contra él con el `fetch` real, sin
// dobles. El día que llegue la especificación del binario, se cambia el server
// de prueba y los tests dicen exactamente qué hay que tocar acá.
import type {
  ReplayCamera,
  ReplayCameraGroup,
  ReplayClipJob,
  ReplayClipPort,
  ReplayClipRequest,
  ReplayClipState,
  ReplayMarkRequest,
  ReplayMarkResult,
} from '@scout/core';

/** Rutas de la API del server de replay, relativas a `baseUrl`. Overrideables porque el server puede variar. */
export interface ReplayPaths {
  status: string;
  mark: string;
  seek: string;
  exportClip: string;
  /** Catálogo de cámaras. */
  cameras: string;
  /**
   * Estado de un job de export. Si trae `{jobId}`, se reemplaza por el id ya
   * escapado; si no lo trae (server que lo pide por query, por ejemplo), el id
   * se cuelga al final como un segmento más. Así entran las dos formas sin
   * pedirle al que configura que conozca este archivo.
   */
  clipStatus: string;
}

const DEFAULT_PATHS: ReplayPaths = {
  status: '/api/status',
  mark: '/api/mark',
  seek: '/api/seek',
  exportClip: '/api/export/clip',
  cameras: '/api/cameras',
  clipStatus: '/api/export/clip/{jobId}',
};

const DEFAULT_TIMEOUT_MS = 1500;

/**
 * Timeout de las operaciones de CLIP (catálogo de cámaras, pedido de export y
 * polling del job).
 *
 * Los 1500 ms de arriba están calibrados para el `mark` fire-and-forget de la
 * captura, donde perder la carrera sólo cuesta un `videoNs` y la regla es que
 * el tecleo no espere a nadie. Pedir un export es lo contrario: es una acción
 * explícita de una persona que está mirando la pantalla, el server tiene que
 * abrir el archivo y encolar trabajo, y cortarla a 1,5 s sería inventar un
 * error donde había una espera normal. La spec del acta lo pide con todas las
 * letras: la revisión necesita su propio perfil de timeouts (§6.3).
 */
const DEFAULT_CLIP_TIMEOUT_MS = 8000;

/** Cuántos caracteres del body de error se conservan en ReplayError.body. */
const ERROR_BODY_TRUNCATE_LENGTH = 500;

export interface ReplayClientOptions {
  /** Base de la API del server de replay, ej. 'http://192.168.1.50:8080'. */
  baseUrl: string;
  /** Token Bearer; si no se pasa, no se manda header Authorization. */
  token?: string;
  /** Timeout por request en ms (default 1500). Es el perfil del `mark`. */
  timeoutMs?: number;
  /**
   * Timeout de las operaciones de clip —`listCameras`, `exportClip`,
   * `clipStatus`— en ms (default 8000). Ver `DEFAULT_CLIP_TIMEOUT_MS`.
   */
  clipTimeoutMs?: number;
  /** Implementación de fetch a usar (inyectable para tests); default fetch global. */
  fetchImpl?: typeof fetch;
  /** Overrides parciales de las rutas default. */
  paths?: Partial<ReplayPaths>;
}

/**
 * Error de la API de replay: se lanza ante cualquier respuesta HTTP no-2xx
 * (excepto en `isAvailable`, que nunca lanza). Incluye el status y un
 * fragmento del body para debug, sin volcar respuestas gigantes al log.
 */
export class ReplayError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(message: string, status: number, body: string) {
    super(message);
    this.name = 'ReplayError';
    this.status = status;
    this.body = body;
  }
}

/** Trunca el body de una respuesta de error para el mensaje/campo de ReplayError. */
function truncateBody(text: string): string {
  if (text.length <= ERROR_BODY_TRUNCATE_LENGTH) return text;
  return `${text.slice(0, ERROR_BODY_TRUNCATE_LENGTH)}…`;
}

/** Extrae markerId aceptando las variantes de nombre que puede usar el server. */
function pickMarkerId(payload: Record<string, unknown>): string | undefined {
  const value = payload['id'] ?? payload['markerId'] ?? payload['marker_id'];
  return typeof value === 'string' ? value : value != null ? String(value) : undefined;
}

/** Extrae videoNs (timecode ns) aceptando las variantes de nombre que puede usar el server. */
function pickVideoNs(payload: Record<string, unknown>): number | undefined {
  const value = payload['ns'] ?? payload['videoNs'] ?? payload['video_ns'];
  return typeof value === 'number' ? value : undefined;
}

/** Extrae jobId aceptando las variantes de nombre que puede usar el server. */
function pickJobId(payload: Record<string, unknown>): string | undefined {
  const value = payload['jobId'] ?? payload['job_id'];
  return typeof value === 'string' ? value : value != null ? String(value) : undefined;
}

/** Primer string no vacío de una lista de claves candidatas. */
function pickString(payload: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (typeof value === 'number') return String(value);
  }
  return undefined;
}

/** Primer número finito de una lista de claves candidatas. */
function pickNumber(payload: Record<string, unknown>, keys: readonly string[]): number | undefined {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

const CAMERA_GROUPS: readonly ReplayCameraGroup[] = ['LINE', 'NET', 'BLOCK', 'FLOOR', 'WIDE'];

/**
 * Grupo funcional de la cámara. Lo que no reconocemos entra como `WIDE` (plano
 * general) en vez de descartar la cámara: una cámara sin grupo sigue sirviendo
 * para pedir el clip, y esconderla dejaría al operador sin un feed que existe.
 */
function pickCameraGroup(payload: Record<string, unknown>): ReplayCameraGroup {
  const raw = pickString(payload, ['group', 'type', 'kind'])?.toUpperCase();
  const known = CAMERA_GROUPS.find((g) => g === raw);
  return known ?? 'WIDE';
}

/** Una entrada del catálogo de cámaras, tolerante con los nombres de campo. */
function toCamera(raw: unknown): ReplayCamera | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const payload = raw as Record<string, unknown>;
  const id = pickString(payload, ['id', 'cameraId', 'camera_id', 'name']);
  if (id === undefined) return null;
  return {
    id,
    label: pickString(payload, ['label', 'name', 'title']) ?? id,
    group: pickCameraGroup(payload),
    fps: pickNumber(payload, ['fps', 'frameRate', 'frame_rate']) ?? 0,
  };
}

/**
 * Sinónimos de estado del job. El server puede decir `processing`, `ready` o
 * `failed`; el contrato del puerto son cuatro estados y la UI dibuja esos.
 */
const CLIP_STATES: Readonly<Record<string, ReplayClipState>> = {
  queued: 'queued',
  pending: 'queued',
  waiting: 'queued',
  running: 'running',
  processing: 'running',
  encoding: 'running',
  in_progress: 'running',
  done: 'done',
  ready: 'done',
  complete: 'done',
  completed: 'done',
  finished: 'done',
  error: 'error',
  failed: 'error',
  failure: 'error',
};

function pickClipState(payload: Record<string, unknown>): ReplayClipState | undefined {
  const raw = pickString(payload, ['state', 'status'])?.toLowerCase();
  return raw === undefined ? undefined : CLIP_STATES[raw];
}

/**
 * Avance normalizado a 0..1. Un server que informa `85` (por ciento) y otro que
 * informa `0.85` dicen lo mismo; se distingue por el rango, que es el único
 * dato disponible. Fuera de rango se descarta antes que dibujar una barra al
 * 8500 %.
 */
function pickProgress(payload: Record<string, unknown>): number | undefined {
  const raw = pickNumber(payload, ['progress', 'percent', 'percentage']);
  if (raw === undefined || raw < 0) return undefined;
  if (raw <= 1) return raw;
  if (raw <= 100) return raw / 100;
  return undefined;
}

/**
 * Crea un cliente HTTP para el server de replay VAR que implementa
 * `ReplayClipPort` (o sea `ReplayPort` + catálogo de cámaras + estado del job).
 * Todas las requests usan AbortController para respetar el timeout que
 * corresponde —corto para `mark`, largo para las de clip— y sólo agregan el
 * header Authorization cuando hay `token`.
 */
export function createReplayClient(opts: ReplayClientOptions): ReplayClipPort {
  const baseUrl = opts.baseUrl.replace(/\/+$/, '');
  const token = opts.token;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const clipTimeoutMs = opts.clipTimeoutMs ?? DEFAULT_CLIP_TIMEOUT_MS;
  const doFetch = opts.fetchImpl ?? fetch;
  const paths: ReplayPaths = { ...DEFAULT_PATHS, ...opts.paths };

  /** Ruta del estado de un job: `{jobId}` si el template lo tiene, sufijo si no. */
  function clipStatusPath(jobId: string): string {
    const escaped = encodeURIComponent(jobId);
    const template = paths.clipStatus;
    if (template.includes('{jobId}')) return template.replace('{jobId}', escaped);
    return `${template.replace(/\/+$/, '')}/${escaped}`;
  }

  /**
   * URL de descarga absoluta. El server puede contestar `/files/clip-7.mp4`
   * (relativa a él) o una URL completa; el que la recibe está en otra máquina y
   * necesita siempre la absoluta.
   */
  function absoluteUrl(raw: string | undefined): string | undefined {
    if (raw === undefined) return undefined;
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return raw;
    return raw.startsWith('/') ? `${baseUrl}${raw}` : `${baseUrl}/${raw}`;
  }

  /** Arma los headers comunes: Content-Type siempre, Authorization sólo si hay token. */
  function buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  /**
   * Ejecuta una request contra el server de replay con timeout via AbortController.
   * Devuelve la Response cruda (2xx o no); el llamador decide qué hacer con
   * códigos de error, salvo para `isAvailable`, que usa su propio wrapper.
   */
  async function request(
    path: string,
    init: { method: 'GET' | 'POST'; body?: unknown; timeoutMs?: number },
  ): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), init.timeoutMs ?? timeoutMs);
    try {
      return await doFetch(`${baseUrl}${path}`, {
        method: init.method,
        headers: buildHeaders(),
        body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }
  }

  /** Lanza ReplayError si la respuesta no es 2xx; si no, devuelve el JSON parseado. */
  async function requestJson<T = unknown>(
    path: string,
    init: { method: 'GET' | 'POST'; body?: unknown; timeoutMs?: number },
  ): Promise<T> {
    const res = await request(path, init);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new ReplayError(
        `Replay server respondió ${res.status} en ${path}`,
        res.status,
        truncateBody(text),
      );
    }
    // Algunas rutas (ej. seek) pueden no devolver body.
    const text = await res.text().catch(() => '');
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  return {
    async isAvailable(): Promise<boolean> {
      // Nunca lanza: cualquier error de red, timeout o status no-2xx ⇒ false.
      try {
        const res = await request(paths.status, { method: 'GET' });
        return res.ok;
      } catch {
        return false;
      }
    },

    async mark(req: ReplayMarkRequest): Promise<ReplayMarkResult> {
      const payload = await requestJson<Record<string, unknown>>(paths.mark, {
        method: 'POST',
        body: {
          label: req.label,
          tag: req.tag,
          pre_s: req.preSeconds,
          post_s: req.postSeconds,
        },
      });
      const markerId = pickMarkerId(payload);
      const videoNs = pickVideoNs(payload);
      if (markerId === undefined || videoNs === undefined) {
        throw new ReplayError(
          `Respuesta de mark sin id/ns reconocibles`,
          0,
          truncateBody(JSON.stringify(payload)),
        );
      }
      return { markerId, videoNs };
    },

    async seek(videoNs: number): Promise<void> {
      await requestJson(paths.seek, { method: 'POST', body: { ns: videoNs } });
    },

    async exportClip(req: ReplayClipRequest): Promise<{ jobId: string }> {
      const payload = await requestJson<Record<string, unknown>>(paths.exportClip, {
        method: 'POST',
        timeoutMs: clipTimeoutMs,
        body: {
          in_ns: req.inNs,
          out_ns: req.outNs,
          cameras: req.cameras,
          label: req.label,
        },
      });
      const jobId = pickJobId(payload);
      if (jobId === undefined) {
        throw new ReplayError(
          `Respuesta de export/clip sin job_id/jobId reconocible`,
          0,
          truncateBody(JSON.stringify(payload)),
        );
      }
      return { jobId };
    },

    async listCameras(): Promise<ReplayCamera[]> {
      // Se acepta `{cameras: [...]}` y el array pelado: son las dos formas en
      // que un server así suele contestar y elegir una sola sería adivinar.
      const payload = await requestJson<unknown>(paths.cameras, {
        method: 'GET',
        timeoutMs: clipTimeoutMs,
      });
      const list = Array.isArray(payload)
        ? payload
        : typeof payload === 'object' && payload !== null
          ? (payload as Record<string, unknown>)['cameras']
          : undefined;
      if (!Array.isArray(list)) {
        throw new ReplayError(
          'Respuesta de cameras sin lista reconocible',
          0,
          truncateBody(JSON.stringify(payload)),
        );
      }
      // Una entrada sin id no es una cámara: se descarta esa, no el catálogo.
      return list.map(toCamera).filter((camera): camera is ReplayCamera => camera !== null);
    },

    async clipStatus(jobId: string): Promise<ReplayClipJob> {
      // `?? {}` porque una respuesta 2xx SIN body es un estado no reconocible,
      // no un crash: cae en el `ReplayError` de abajo, con su motivo.
      const payload =
        (await requestJson<Record<string, unknown>>(clipStatusPath(jobId), {
          method: 'GET',
          timeoutMs: clipTimeoutMs,
        })) ?? {};
      const state = pickClipState(payload);
      if (state === undefined) {
        // Un estado que no entendemos NO se degrada a `queued`: eso deja la UI
        // esperando para siempre un clip que nunca va a llegar.
        throw new ReplayError(
          `Respuesta de clipStatus sin estado reconocible`,
          0,
          truncateBody(JSON.stringify(payload)),
        );
      }
      const url = absoluteUrl(pickString(payload, ['url', 'downloadUrl', 'download_url', 'file']));
      const progress = pickProgress(payload);
      const error = pickString(payload, ['error', 'message', 'reason']);
      return {
        jobId: pickJobId(payload) ?? jobId,
        state,
        ...(state === 'done' && url !== undefined ? { url } : {}),
        ...(progress !== undefined ? { progress } : {}),
        ...(state === 'error' && error !== undefined ? { error } : {}),
      };
    },
  };
}
